import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import ScheduleSendOutlined from "@mui/icons-material/ScheduleSendOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { PLATFORM_CONNECT } from "../../socialScheduler/platformConnectConfig";

export default function ShareClipModal({ open, onClose, clip, lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [platforms, setPlatforms] = React.useState([]);
  const [connected, setConnected] = React.useState({});
  const [caption, setCaption] = React.useState("");
  const [scheduledAt, setScheduledAt] = React.useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [loadingPlatforms, setLoadingPlatforms] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setCaption(String(clip?.description || clip?.title || "").trim());
    setPlatforms([]);
    setLoadingPlatforms(true);
    (async () => {
      try {
        const res = await hitAxios({
          path: "/api/social-scheduler/platforms",
          post: false,
          showLoading: false,
          showSnackbar: false,
        });
        if (res?.data?.success) {
          const list = res.data.data?.platforms || [];
          const map = {};
          list.forEach((p) => {
            map[p.id] = Boolean(p.connected);
          });
          setConnected(map);
        }
      } finally {
        setLoadingPlatforms(false);
      }
    })();
  }, [open, clip, hitAxios]);

  const togglePlatform = (id) => {
    setPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  async function handleGenerateCaption() {
    if (!clip?.video_url) return;
    setGenerating(true);
    try {
      const res = await hitAxios({
        path: "/api/social-scheduler/generate-caption",
        post: true,
        obj: {
          video_url: clip.video_url,
          post_type: "video",
          video_title: clip.title || "",
          description: clip.description || clip.reason || "",
        },
        showLoading: false,
      });
      if (res?.data?.success) {
        const d = res.data.data;
        setCaption([d.caption, d.hashtags].filter(Boolean).join("\n\n"));
        showSnack?.(lang?.socialCaptionGenerated || "Caption generated!", true);
      } else {
        showSnack?.(res?.data?.msg || "Caption generation failed", false);
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleSchedule() {
    if (!clip?.video_url) {
      showSnack?.(lang?.socialNeedVideo || "No video URL available.", false);
      return;
    }
    if (!caption.trim()) {
      showSnack?.(lang?.socialNeedCaption || "Write or generate a caption", false);
      return;
    }
    if (!platforms.length) {
      showSnack?.(lang?.socialNeedPlatforms || "Select at least one platform", false);
      return;
    }

    setSubmitting(true);
    try {
      const res = await hitAxios({
        path: "/api/social-scheduler",
        post: true,
        obj: {
          video_url: clip.video_url,
          post_type: "video",
          caption: caption.trim(),
          platforms,
          scheduled_at: new Date(scheduledAt).toISOString(),
          product_name: clip.title || "",
          description: clip.description || "",
        },
      });
      if (res?.data?.success) {
        showSnack?.(lang?.socialScheduled || "Post scheduled!", true);
        onClose?.();
      } else {
        showSnack?.(res?.data?.msg || "Could not schedule post.", false);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {lang?.aiClippingShareTitle || "Share clip"}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {clip?.title}
          </Typography>

          {clip?.video_url && (
            <Box
              component="video"
              src={clip.video_url}
              controls
              playsInline
              preload="metadata"
              sx={{ width: "100%", maxHeight: 280, borderRadius: 2, bgcolor: "black" }}
            />
          )}

          <TextField
            label={lang?.socialCaption || "Caption"}
            multiline
            minRows={4}
            fullWidth
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={generating ? <CircularProgress size={16} /> : <AutoAwesomeOutlined />}
            onClick={handleGenerateCaption}
            disabled={generating}
            sx={{ alignSelf: "flex-start", borderRadius: 2, fontWeight: 700 }}
          >
            {lang?.socialGenerateCaption || "Generate caption"}
          </Button>

          <Box>
            <Typography variant="caption" fontWeight={700} display="block" mb={1}>
              {lang?.socialPlatforms || "Platforms"}
            </Typography>
            {loadingPlatforms ? (
              <CircularProgress size={20} />
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {PLATFORM_CONNECT.map((p) => (
                  <Chip
                    key={p.id}
                    label={`${p.emoji} ${p.label}`}
                    clickable
                    color={platforms.includes(p.id) ? "primary" : "default"}
                    variant={platforms.includes(p.id) ? "filled" : "outlined"}
                    onClick={() => togglePlatform(p.id)}
                    title={
                      connected[p.id]
                        ? undefined
                        : lang?.socialConnectFirst || "Connect this account in Scheduler first"
                    }
                  />
                ))}
              </Stack>
            )}
          </Box>

          <TextField
            label={lang?.socialScheduledAt || "Schedule for"}
            type="datetime-local"
            fullWidth
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ScheduleSendOutlined />}
          onClick={handleSchedule}
          disabled={submitting}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {lang?.socialSchedulePost || "Schedule post"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
